import React, { useState } from 'react';

import { Collapse } from '@material-ui/core';

import clsx from 'clsx';

import { useTheme, useId } from '@coursera/cds-common';
import type { ForwardRefComponent } from '@coursera/cds-common';
import { ChevronNextIcon } from '@coursera/cds-icons';

import Button from '@core/Button';

import getShowMoreContainerCss, { classes } from './getShowMoreContainerCss';

export type Props = {
  /**
   * Label for the CTA button when expanded.
   */
  expandedLabel: string;

  /**
   * Label for the CTA button when collapsed.
   */
  collapsedLabel: string;

  /**
   * Free space container for any content that is disclosed on expand.
   */
  children: React.ReactNode;

  /**
   * Set on the CTA button to call out a custom label for screen-readers.
   * This is useful if the `collapsedLabel` is long and we don't want to
   * call out the entire string (e.g. "Info about SkillSets" when the label
   * says "Show more information about the Coursera SkillSets program").
   *
   * Optional but highly recommended to be passed in. If not, the CTA label will be called out.
   */
  'aria-label'?: string;

  /**
   * Defines what styling to use for the content container.
   * @default standard
   */
  variant?: 'standard' | 'silent';

  /**
   * Whether the component should load in expanded state by default.
   * @default false
   */
  defaultExpanded?: boolean;

  /**
   * Callback for clients to listen to component state changes i.e. when the component is expanded/collapsed.
   * @param {boolean} isExpanded new expanded state on change
   */
  onChange?: (isExpanded: boolean) => void;

  /**
   * base ID to be used for a11y, will be used as a prefix for subcomponents. By default, it will be autogenerated in CDS.
   * @ignore
   */
  id?: string;
};

type ShowMoreContainerType = ForwardRefComponent<HTMLDivElement, Props>;

/**
 * Enables content to be collapsed (hidden) by default to break up information and choices that are secondary to the core experience.
 * Giving the user complete control on when to review more information enables a simple yet powerful way to prioritize the user’s
 * attention on the main features.
 *
 * See [Props](__storybookUrl__/components-surfaces-show-more-container--default#props)
 *
 * @example
 * <ShowMoreContainer
 *   collapsedLabel="Show info about SkillSets"
 *   expandedLabel="Hide info about SkillSets"
 *   aria-label="SkillSets"
 * >
 *  <p> More detailed content about SkillSets </p>
 * </ShowMoreContainer>
 */
const ShowMoreContainer: ShowMoreContainerType = React.forwardRef<
  HTMLDivElement,
  Props
>(function ShowMoreContainer(
  {
    variant = 'standard',
    expandedLabel,
    collapsedLabel,
    defaultExpanded = false,
    onChange,
    children,
    id,
    ...restProps
  },
  ref
) {
  const [isExpanded, setExpanded] = useState(defaultExpanded);
  const theme = useTheme();

  const onExpandChange = () => {
    setExpanded((previousExpanded) => !previousExpanded);
    if (onChange) {
      onChange(!isExpanded);
    }
  };

  // unique ids used for a11y
  const baseId = useId(id);
  const ctaId = `${baseId}-cta`;
  const contentId = `${baseId}-content`;

  return (
    <div
      ref={ref}
      css={getShowMoreContainerCss(theme)}
      id={baseId}
      {...restProps}
    >
      <Button
        aria-controls={contentId}
        aria-expanded={isExpanded}
        aria-label={restProps['aria-label'] ?? undefined}
        className={classes.ctaButton}
        icon={
          <ChevronNextIcon
            className={clsx(classes.ctaIcon, {
              expanded: isExpanded,
            })}
            size="small"
          />
        }
        iconPosition="before"
        id={ctaId}
        size="small"
        variant="ghost"
        onClick={onExpandChange}
      >
        {isExpanded ? expandedLabel : collapsedLabel}
      </Button>
      <Collapse in={isExpanded} timeout="auto">
        <div
          aria-hidden={!isExpanded}
          aria-labelledby={ctaId}
          className={clsx(classes.content, {
            [classes.standard]: variant === 'standard',
            [classes.silent]: variant === 'silent',
          })}
          id={contentId}
          role="region"
        >
          {children}
        </div>
      </Collapse>
    </div>
  );
});

export default ShowMoreContainer;
